import React, { useState, useEffect } from 'react';
import {
  X,
  Settings,
  Languages,
  LayoutGrid,
  RotateCcw,
  Sparkles,
  Check,
  Compass,
  Database,
  ExternalLink,
  Shield,
  Layers,
  HelpCircle,
  MessageSquare,
  MapPin,
  Clock,
  Search,
  Navigation,
  Globe,
  Info,
  CheckCircle2,
  Sliders,
  Activity,
  Timer,
  BookOpen,
  Zap
} from 'lucide-react';
import { ChartStyle, Language, MenuLayoutMode } from '../types/kundali';
import {
  getTimeFormatPref,
  setTimeFormatPref,
  TimeFormatPreference,
  formatTime
} from '../utils/dateUtils';
import {
  CANONICAL_CITY_PRESETS,
  COMMON_TIMEZONES,
  VerifiedLocation,
  getUserLocationPref,
  setUserLocationPref,
  searchCanonicalLocations,
  formatVerifiedCoordinates
} from '../services/geoService';
import { getAppVersionInfo } from '../utils/versionManager';
import { getMenuLayoutModePref, setMenuLayoutModePref } from '../utils/layoutPreferences';

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Language;
  setLang: (lang: Language) => void;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  menuLayoutMode?: MenuLayoutMode;
  onMenuLayoutModeChange?: (mode: MenuLayoutMode) => void;
  onOpenLegacyDashboard?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenVersionModal?: () => void;
  isSuperAdmin?: boolean;
}

type SettingsSection = 'chartStyle' | 'navtara' | 'location' | 'calculation' | 'language' | 'legacy';

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  lang,
  setLang,
  chartStyle,
  setChartStyle,
  menuLayoutMode,
  onMenuLayoutModeChange,
  onOpenLegacyDashboard,
  onOpenWhatsAppModal,
  onOpenVersionModal,
  isSuperAdmin = false
}) => {
  const isHi = lang === 'hi';
  const [activeSection, setActiveSection] = useState<SettingsSection>('chartStyle');

  // Local state for calculation preferences with persistent localStorage
  const [ayanamsha, setAyanamsha] = useState<string>(() => {
    return localStorage.getItem('astro_ayanamsha_pref') || 'lahiri';
  });
  const [nodeType, setNodeType] = useState<string>(() => {
    return localStorage.getItem('astro_node_pref') || 'mean';
  });
  const [showDegreesPref, setShowDegreesPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_degrees_pref') !== 'false';
  });
  const [antiOverlapPref, setAntiOverlapPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_anti_overlap_pref') !== 'false';
  });
  const [showAskAcharyaPref, setShowAskAcharyaPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_show_ask_acharya') !== 'false';
  });
  const [showNavtaraHoverPref, setShowNavtaraHoverPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_hover_pref') !== 'false';
  });
  const [navtaraFlashPref, setNavtaraFlashPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_flash_pref') !== 'false';
  });
  const [deductRahuKalamPref, setDeductRahuKalamPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_deduct_rahu') !== 'false';
  });
  const [navtaraMotionBadgesPref, setNavtaraMotionBadgesPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_motion_badges_pref') !== 'false';
  });
  const [navtaraAutoCollapseGuidePref, setNavtaraAutoCollapseGuidePref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_guide_autocollapse') !== 'false';
  });
  const [navtaraLordDeityPref, setNavtaraLordDeityPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_show_lord_deity') !== 'false';
  });
  const [inspectorViewPref, setInspectorViewPref] = useState<'collapsed' | 'expanded'>(() => {
    return (localStorage.getItem('astro_inspector_default_view') as 'collapsed' | 'expanded') || 'collapsed';
  });
  const [timeFormatPref, setTimeFormatPrefState] = useState<TimeFormatPreference>(getTimeFormatPref);

  // Menu & Navigation Layout Mode Preference (Default: 'horizontal')
  const [menuLayout, setMenuLayout] = useState<MenuLayoutMode>(() => {
    return menuLayoutMode || getMenuLayoutModePref();
  });

  useEffect(() => {
    if (menuLayoutMode && menuLayoutMode !== menuLayout) {
      setMenuLayout(menuLayoutMode);
    }
  }, [menuLayoutMode]);

  const handleSelectMenuLayout = (mode: MenuLayoutMode) => {
    setMenuLayout(mode);
    setMenuLayoutModePref(mode);
    if (onMenuLayoutModeChange) {
      onMenuLayoutModeChange(mode);
    }
    setSaveSuccessMsg(
      isHi
        ? (mode === 'horizontal' ? 'क्षैतिज मेनू लेआउट (Horizontal) सक्रिय किया गया' : 'वर्टिकल साइडबार लेआउट (Vertical) सक्रिय किया गया')
        : (mode === 'horizontal' ? 'Switched to Horizontal Menu Layout (Classic)' : 'Switched to Vertical Sidebar Layout (Collapsible)')
    );
    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  // Location & Timezone Preferences
  const [userLocation, setUserLocation] = useState<VerifiedLocation>(getUserLocationPref);
  const [searchCityQuery, setSearchCityQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState<VerifiedLocation[]>([]);
  const [isSearchingCity, setIsSearchingCity] = useState(false);
  const [presetFilter, setPresetFilter] = useState<'all' | 'india' | 'global'>('all');
  const [isCustomCoordOpen, setIsCustomCoordOpen] = useState(false);
  const [customLatStr, setCustomLatStr] = useState(String(userLocation.latitude));
  const [customLngStr, setCustomLngStr] = useState(String(userLocation.longitude));
  const [customPlaceNameStr, setCustomPlaceNameStr] = useState(userLocation.placeName);
  const [customTimezoneOffset, setCustomTimezoneOffset] = useState<number>(userLocation.timezoneOffsetMinutes);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  // Live clock in user's selected location timezone
  const [localTimeStr, setLocalTimeStr] = useState<string>('');

  useEffect(() => {
    const updateLocalTime = () => {
      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const targetDate = new Date(utc + userLocation.timezoneOffsetMinutes * 60000);
      setLocalTimeStr(formatTime(targetDate, true, timeFormatPref));
    };
    updateLocalTime();
    const interval = setInterval(updateLocalTime, 1000);
    return () => clearInterval(interval);
  }, [userLocation.timezoneOffsetMinutes, isHi, timeFormatPref]);

  // Debounced search for locations
  useEffect(() => {
    if (!searchCityQuery || searchCityQuery.trim().length < 2) {
      setCitySearchResults([]);
      setIsSearchingCity(false);
      return;
    }
    setIsSearchingCity(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchCanonicalLocations(searchCityQuery);
        setCitySearchResults(results);
      } catch (err) {
        console.warn('City search failed:', err);
      } finally {
        setIsSearchingCity(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchCityQuery]);

  const handleSelectLocation = (loc: VerifiedLocation) => {
    setUserLocation(loc);
    setUserLocationPref(loc);
    setCustomLatStr(String(loc.latitude));
    setCustomLngStr(String(loc.longitude));
    setCustomPlaceNameStr(loc.placeName);
    setCustomTimezoneOffset(loc.timezoneOffsetMinutes);
    setSearchCityQuery('');
    setCitySearchResults([]);
    setSaveSuccessMsg(isHi ? `स्थान बदलकर "${loc.name}" किया गया` : `Location updated to ${loc.name}`);
    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  const handleTimezoneChange = (offsetMinutes: number, tzCode: string, tzLabel: string) => {
    const updated: VerifiedLocation = {
      ...userLocation,
      timezoneOffsetMinutes: offsetMinutes,
      timezoneName: `${tzCode} (${tzLabel.split(' ')[0]})`
    };
    setUserLocation(updated);
    setUserLocationPref(updated);
    setCustomTimezoneOffset(offsetMinutes);
    setSaveSuccessMsg(isHi ? `समय क्षेत्र बदलकर ${tzLabel} किया गया` : `Timezone updated to ${tzLabel}`);
    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  const handleSaveCustomCoords = () => {
    const lat = parseFloat(customLatStr);
    const lng = parseFloat(customLngStr);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert(isHi ? 'कृपया वैध अक्षांश (-90 से 90) और देशांतर (-180 से 180) दर्ज करें' : 'Please enter valid Latitude (-90 to 90) and Longitude (-180 to 180)');
      return;
    }
    const matchedTz = COMMON_TIMEZONES.find(t => t.offsetMinutes === customTimezoneOffset);
    const updated: VerifiedLocation = {
      name: customPlaceNameStr.split(',')[0].trim() || 'Custom Location',
      placeName: customPlaceNameStr.trim() || 'Custom Location',
      latitude: lat,
      longitude: lng,
      timezoneOffsetMinutes: customTimezoneOffset,
      timezoneName: matchedTz ? `${matchedTz.code} (${matchedTz.label.split(' ')[0]})` : `UTC${customTimezoneOffset >= 0 ? '+' : ''}${(customTimezoneOffset / 60).toFixed(1)}`
    };
    setUserLocation(updated);
    setUserLocationPref(updated);
    setIsCustomCoordOpen(false);
    setSaveSuccessMsg(isHi ? 'कस्टम स्थान व निर्देशांक सफलतापूर्वक सहेजे गए' : 'Custom coordinates saved successfully');
    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  const handleDetectGps = () => {
    if (!navigator.geolocation) {
      alert(isHi ? 'आपके ब्राउज़र में GPS सुविधा उपलब्ध नहीं है' : 'Geolocation is not supported by your browser');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const browserTzOffset = -new Date().getTimezoneOffset();
        
        try {
          const results = await searchCanonicalLocations(`${lat.toFixed(2)}, ${lng.toFixed(2)}`);
          const found = results.find(r => Math.abs(r.latitude - lat) < 0.5 && Math.abs(r.longitude - lng) < 0.5);
          const newLoc: VerifiedLocation = found ? {
            ...found,
            latitude: lat,
            longitude: lng,
            timezoneOffsetMinutes: browserTzOffset
          } : {
            name: 'Current Device Location',
            placeName: `GPS (${lat.toFixed(3)}°, ${lng.toFixed(3)}°)`,
            latitude: lat,
            longitude: lng,
            timezoneOffsetMinutes: browserTzOffset,
            timezoneName: `Local (UTC${browserTzOffset >= 0 ? '+' : ''}${(browserTzOffset / 60).toFixed(1)})`
          };
          handleSelectLocation(newLoc);
        } catch {
          const newLoc: VerifiedLocation = {
            name: 'Current Device Location',
            placeName: `GPS (${lat.toFixed(3)}°, ${lng.toFixed(3)}°)`,
            latitude: lat,
            longitude: lng,
            timezoneOffsetMinutes: browserTzOffset,
            timezoneName: `Local (UTC${browserTzOffset >= 0 ? '+' : ''}${(browserTzOffset / 60).toFixed(1)})`
          };
          handleSelectLocation(newLoc);
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        setGpsLoading(false);
        console.warn('GPS error:', err);
        alert(isHi ? 'GPS स्थान प्राप्त करने की अनुमति नहीं मिली।' : 'GPS location permission denied or timed out.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSaveAyanamsha = (val: string) => {
    setAyanamsha(val);
    try {
      localStorage.setItem('astro_ayanamsha_pref', val);
      window.dispatchEvent(new Event('astro_calculation_pref_changed'));
    } catch {}
  };

  const handleResetCalculationDefaults = () => {
    setAyanamsha('lahiri');
    setNodeType('mean');
    setShowDegreesPref(true);
    setAntiOverlapPref(true);
    try {
      localStorage.setItem('astro_ayanamsha_pref', 'lahiri');
      localStorage.setItem('astro_node_pref', 'mean');
      localStorage.setItem('astro_degrees_pref', 'true');
      localStorage.setItem('astro_anti_overlap_pref', 'true');
      window.dispatchEvent(new Event('astro_calculation_pref_changed'));
      window.dispatchEvent(new Event('astro_degrees_pref_changed'));
      window.dispatchEvent(new Event('astro_anti_overlap_pref_changed'));
    } catch {}
  };

  const handleSaveNodeType = (val: string) => {
    setNodeType(val);
    try {
      localStorage.setItem('astro_node_pref', val);
      window.dispatchEvent(new Event('astro_calculation_pref_changed'));
    } catch {}
  };

  const handleSaveShowDegrees = (val: boolean) => {
    setShowDegreesPref(val);
    try {
      localStorage.setItem('astro_degrees_pref', String(val));
      window.dispatchEvent(new Event('astro_degrees_pref_changed'));
    } catch {}
  };

  const handleSaveAntiOverlap = (val: boolean) => {
    setAntiOverlapPref(val);
    try {
      localStorage.setItem('astro_anti_overlap_pref', String(val));
      window.dispatchEvent(new Event('astro_anti_overlap_pref_changed'));
    } catch {}
  };

  const handleSaveShowAskAcharya = (val: boolean) => {
    setShowAskAcharyaPref(val);
    try {
      localStorage.setItem('astro_show_ask_acharya', String(val));
      window.dispatchEvent(new Event('astro_show_ask_acharya_changed'));
    } catch {}
  };

  const handleSaveShowNavtaraHover = (val: boolean) => {
    setShowNavtaraHoverPref(val);
    try {
      localStorage.setItem('astro_navtara_hover_pref', String(val));
      window.dispatchEvent(new Event('astro_navtara_hover_pref_changed'));
    } catch {}
  };

  const handleSaveNavtaraFlash = (val: boolean) => {
    setNavtaraFlashPref(val);
    try {
      localStorage.setItem('astro_navtara_flash_pref', String(val));
      window.dispatchEvent(new Event('astro_navtara_flash_pref_changed'));
    } catch {}
  };

  const handleSaveDeductRahuKalam = (val: boolean) => {
    setDeductRahuKalamPref(val);
    try {
      localStorage.setItem('astro_navtara_deduct_rahu', String(val));
      window.dispatchEvent(new Event('astro_navtara_deduct_rahu_changed'));
    } catch {}
  };

  const handleSaveNavtaraMotionBadges = (val: boolean) => {
    setNavtaraMotionBadgesPref(val);
    try {
      localStorage.setItem('astro_navtara_motion_badges_pref', String(val));
      window.dispatchEvent(new Event('astro_navtara_motion_badges_pref_changed'));
    } catch {}
  };

  const handleSaveNavtaraAutoCollapseGuide = (val: boolean) => {
    setNavtaraAutoCollapseGuidePref(val);
    try {
      localStorage.setItem('astro_navtara_guide_autocollapse', String(val));
      window.dispatchEvent(new Event('astro_navtara_guide_autocollapse_changed'));
    } catch {}
  };

  const handleSaveNavtaraLordDeity = (val: boolean) => {
    setNavtaraLordDeityPref(val);
    try {
      localStorage.setItem('astro_navtara_show_lord_deity', String(val));
      window.dispatchEvent(new Event('astro_navtara_show_lord_deity_changed'));
    } catch {}
  };

  const handleSaveInspectorViewPref = (val: 'collapsed' | 'expanded') => {
    setInspectorViewPref(val);
    try {
      localStorage.setItem('astro_inspector_default_view', val);
      window.dispatchEvent(new Event('astro_inspector_view_pref_changed'));
    } catch {}
  };

  const handleSaveTimeFormatPref = (val: TimeFormatPreference) => {
    setTimeFormatPrefState(val);
    setTimeFormatPref(val);
    setSaveSuccessMsg(isHi ? `समय प्रारूप ${val === '12h' ? '12-घंटे (AM/PM)' : '24-घंटे (सैन्य/रेलवे)'} सेट किया गया` : `Time format updated to ${val === '12h' ? '12-Hour (AM/PM)' : '24-Hour (24h)'}`);
    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredPresets = CANONICAL_CITY_PRESETS.filter(p => {
    if (presetFilter === 'india') return p.country === 'India' || p.timezoneOffsetMinutes === 330;
    if (presetFilter === 'global') return p.country !== 'India' && p.timezoneOffsetMinutes !== 330;
    return true;
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/50 backdrop-blur-xs animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
    >
      <div
        className="bg-astro-bg border border-astro-border rounded-2xl w-full max-w-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-astro-border bg-[#FAF6ED]/70">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-astro-primary text-white shadow-xs">
              <Settings className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <h2 id="settings-modal-title" className="text-base sm:text-lg font-bold font-serif text-astro-primary">
                {isHi ? 'प्रणाली एवं ज्योतिषीय सेटिंग्स' : 'System & Astrological Settings'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'सभी प्रोफाइल, कुंडली एवं विश्लेषणात्मक उपकरणों हेतु सार्वभौमिक सेटिंग्स'
                  : 'Universal preferences common across all profiles, charts & real-time ephemeris'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg text-astro-secondary-text hover:text-astro-primary hover:bg-astro-border/50 transition-colors cursor-pointer"
            aria-label={isHi ? 'सेटिंग्स बंद करें' : 'Close settings'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Navigation Tabs */}
        <div className="flex items-center border-b border-astro-border bg-astro-bg px-4 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setActiveSection('chartStyle')}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === 'chartStyle'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'कुंडली शैली (उत्तर / दक्षिण / पूर्व)' : 'Kundali Chart Style'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('navtara')}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === 'navtara'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'नवतारा चक्र एवं नक्षत्र' : 'Navtara & Nakshatras'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('location')}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === 'location'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <MapPin className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'स्थान व समय क्षेत्र (Location & Timezone)' : 'Location & Timezone'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('calculation')}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === 'calculation'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Compass className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'अयनांश व गणना वरीयता' : 'Ayanamsha & Calculation'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('language')}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === 'language'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Languages className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'भाषा (Language)' : 'Language'}</span>
          </button>

          {onOpenLegacyDashboard && isSuperAdmin && (
            <button
              type="button"
              onClick={() => setActiveSection('legacy')}
              className={`flex items-center gap-2 px-3 py-2.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
                activeSection === 'legacy'
                  ? 'border-astro-primary text-astro-primary'
                  : 'border-transparent text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <RotateCcw className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'लेगेसी डैशबोर्ड' : 'Legacy Dashboard'}</span>
            </button>
          )}
        </div>

        {/* Modal Body / Tab Content */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-6">
          {/* Toast Notification Banner */}
          {saveSuccessMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-xl text-xs font-semibold flex items-center gap-2 shadow-xs animate-fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>{saveSuccessMsg}</span>
            </div>
          )}

          {/* SECTION: LOCATION & TIMEZONE */}
          {activeSection === 'location' && (
            <div className="space-y-5">
              {/* Header Info */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-astro-primary" />
                    <span>{isHi ? 'उपयोगकर्ता का स्थान व समय क्षेत्र' : 'User Reference Location & Timezone'}</span>
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? 'पंचांग, सूर्योदय, राहुकाल, अभिजीत मुहूर्त, चौघड़िया एवं गोचर फलित हेतु आपका स्थान'
                      : 'Reference place of action for Panchang, Solar Noon, Rahu Kaal, Choghadiya & Local Muhurta'}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleDetectGps}
                  disabled={gpsLoading}
                  className="px-3 py-1.5 bg-astro-card hover:bg-[#FAF6ED] text-astro-primary rounded-xl border border-astro-border text-xs font-bold flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer self-start sm:self-auto disabled:opacity-50"
                >
                  <Navigation className={`w-3.5 h-3.5 text-astro-accent ${gpsLoading ? 'animate-spin' : ''}`} />
                  <span>{gpsLoading ? (isHi ? 'GPS खोज रहा है...' : 'Detecting GPS...') : (isHi ? 'GPS से मेरा स्थान लें' : 'Detect My Location (GPS)')}</span>
                </button>
              </div>

              {/* Active Current Location Highlight Card */}
              <div className="p-4 rounded-2xl border border-astro-border bg-[#FAF6ED] space-y-3 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-astro-primary text-white flex items-center justify-center shadow-xs shrink-0">
                      <MapPin className="w-5 h-5 text-astro-accent" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-astro-accent block">
                        {isHi ? 'वर्तमान सक्रिय संदर्भ स्थान' : 'Currently Active Reference Location'}
                      </span>
                      <h4 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                        {userLocation.placeName}
                      </h4>
                    </div>
                  </div>

                  {/* Local Live Clock Badge */}
                  <div className="bg-white px-3 py-1.5 rounded-xl border border-astro-border flex items-center gap-2 self-start sm:self-auto shadow-2xs">
                    <Clock className="w-4 h-4 text-astro-primary" />
                    <div>
                      <span className="text-[9px] text-astro-secondary-text uppercase font-semibold block leading-none">
                        {isHi ? 'स्थान का स्थानीय समय' : 'Local Time'}
                      </span>
                      <span className="text-xs font-mono font-bold text-astro-primary">
                        {localTimeStr}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs text-astro-secondary-text pt-0.5">
                  <span className="bg-white px-2.5 py-1 rounded-lg border border-astro-border font-mono text-astro-primary font-semibold">
                    {formatVerifiedCoordinates(userLocation.latitude, userLocation.longitude, userLocation.timezoneOffsetMinutes)}
                  </span>
                  <span className="bg-white px-2.5 py-1 rounded-lg border border-astro-border text-astro-primary font-medium">
                    {userLocation.timezoneName || (userLocation.timezoneOffsetMinutes === 330 ? 'IST (UTC+5:30)' : `UTC Offset: ${userLocation.timezoneOffsetMinutes} mins`)}
                  </span>
                </div>
              </div>

              {/* Search City / Autocomplete Field */}
              <div className="space-y-1.5 relative">
                <label className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'किसी भी शहर को खोजें (Search City or Place)' : 'Search Any City or Place Worldwide'}</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchCityQuery}
                    onChange={(e) => setSearchCityQuery(e.target.value)}
                    placeholder={isHi ? 'उदा. Indore, Ujjain, Bhopal, London, New York, Dubai...' : 'e.g. Indore, Ujjain, London, New York, Dubai, Singapore...'}
                    className="w-full px-3.5 py-2.5 pl-9 rounded-xl border border-astro-border bg-white text-xs text-astro-text placeholder:text-astro-secondary-text/60 focus:outline-hidden focus:ring-2 focus:ring-astro-primary/30"
                  />
                  <Search className="w-4 h-4 text-astro-secondary-text absolute left-3 top-3" />
                  {searchCityQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchCityQuery('')}
                      className="absolute right-3 top-2.5 text-astro-secondary-text hover:text-astro-primary p-0.5"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Autocomplete Results Dropdown */}
                {searchCityQuery.trim().length >= 2 && (
                  <div className="bg-white border border-astro-border rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto divide-y divide-astro-border/40 z-20">
                    {isSearchingCity ? (
                      <div className="p-3 text-center text-xs text-astro-secondary-text flex items-center justify-center gap-2">
                        <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-astro-primary" />
                        <span>{isHi ? 'स्थान खोजे जा रहे हैं...' : 'Searching canonical locations...'}</span>
                      </div>
                    ) : citySearchResults.length > 0 ? (
                      citySearchResults.map((res, i) => (
                        <div
                          key={i}
                          onClick={() => handleSelectLocation(res)}
                          className="p-2.5 hover:bg-astro-bg transition-colors cursor-pointer flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-bold text-astro-primary block">{res.placeName}</span>
                            <span className="text-[10px] text-astro-secondary-text font-mono">
                              {res.latitude.toFixed(2)}°, {res.longitude.toFixed(2)}° • {res.timezoneName || 'IST'}
                            </span>
                          </div>
                          <span className="text-[10px] bg-astro-card px-2 py-0.5 rounded border border-astro-border text-astro-primary font-semibold">
                            {isHi ? 'चुनें' : 'Select'}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-center text-xs text-astro-secondary-text">
                        {isHi ? 'कोई मेल खाता स्थान नहीं मिला। नीचे कस्टम निर्देशांक दर्ज कर सकते हैं।' : 'No matches found. You can enter custom coordinates below.'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Curated Quick Presets */}
              <div className="space-y-2.5 pt-2 border-t border-astro-border">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'प्रमुख शहर एवं ज्योतिषीय केंद्र (Quick Presets)' : 'Prominent Vedic & Global Presets'}</span>
                  </h4>

                  {/* Filter tabs */}
                  <div className="flex items-center gap-1 bg-astro-bg p-0.5 rounded-lg border border-astro-border text-[10px]">
                    <button
                      type="button"
                      onClick={() => setPresetFilter('all')}
                      className={`px-2 py-0.5 rounded font-bold transition-all ${presetFilter === 'all' ? 'bg-astro-primary text-white' : 'text-astro-secondary-text'}`}
                    >
                      {isHi ? 'सभी' : 'All'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetFilter('india')}
                      className={`px-2 py-0.5 rounded font-bold transition-all ${presetFilter === 'india' ? 'bg-astro-primary text-white' : 'text-astro-secondary-text'}`}
                    >
                      {isHi ? 'भारत / अवंतिका' : 'India / Avanti'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetFilter('global')}
                      className={`px-2 py-0.5 rounded font-bold transition-all ${presetFilter === 'global' ? 'bg-astro-primary text-white' : 'text-astro-secondary-text'}`}
                    >
                      {isHi ? 'अंतर्राष्ट्रीय' : 'Global'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto p-0.5 no-scrollbar">
                  {filteredPresets.map((preset, idx) => {
                    const isSelected = Math.abs(preset.latitude - userLocation.latitude) < 0.05 && Math.abs(preset.longitude - userLocation.longitude) < 0.05;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectLocation(preset)}
                        className={`p-2 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                          isSelected
                            ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-2xs'
                            : 'border-astro-border bg-white hover:bg-[#FAF6ED]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-bold text-astro-primary truncate">{preset.name}</span>
                          {isSelected && <Check className="w-3 h-3 text-astro-primary shrink-0" />}
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-astro-secondary-text">
                          <span className="truncate">{preset.country || 'India'}</span>
                          <span className="font-mono text-[9px] bg-astro-bg px-1 rounded border border-astro-border shrink-0">
                            {preset.timezoneOffsetMinutes === 330 ? 'IST' : `UTC${preset.timezoneOffsetMinutes >= 0 ? '+' : ''}${(preset.timezoneOffsetMinutes / 60).toFixed(0)}`}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Timezone Direct Dropdown */}
              <div className="space-y-2 pt-2 border-t border-astro-border">
                <label className="text-xs font-bold text-astro-primary flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'समय क्षेत्र चयन (Standard Timezone Offset)' : 'Standard Timezone Offset'}</span>
                  </span>
                  <span className="text-[10px] text-astro-secondary-text font-normal">
                    {isHi ? 'स्थान का मानक समय' : 'Governs clock conversions'}
                  </span>
                </label>
                <select
                  value={userLocation.timezoneOffsetMinutes}
                  onChange={(e) => {
                    const offset = parseInt(e.target.value, 10);
                    const match = COMMON_TIMEZONES.find(t => t.offsetMinutes === offset);
                    if (match) {
                      handleTimezoneChange(offset, match.code, match.label);
                    }
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-astro-border bg-white text-xs text-astro-text focus:outline-hidden focus:ring-2 focus:ring-astro-primary/30"
                >
                  {COMMON_TIMEZONES.map((tz, i) => (
                    <option key={i} value={tz.offsetMinutes}>
                      {tz.label} — {tz.representativeRegions}
                    </option>
                  ))}
                </select>
              </div>

              {/* Global Time Format Setting (12-Hour AM/PM vs 24-Hour) */}
              <div className="space-y-2 pt-2 border-t border-astro-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'समय प्रदर्शन प्रारूप (Global Time Format)' : 'Global Time Format (AM/PM vs. 24h)'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text">
                      {isHi
                        ? 'पंचांग, होरा, राहुकाल, गोचर एवं मुहूर्त में 12-घंटे (AM/PM) अथवा 24-घंटे प्रारूप'
                        : 'Synchronize 12-hour (AM/PM) or 24-hour time format universally across all widgets'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handleSaveTimeFormatPref('12h')}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                      timeFormatPref === '12h'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-white hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div>
                      <span className="text-xs font-bold text-astro-primary block">
                        {isHi ? '12 घंटे (AM / PM)' : '12-Hour (AM / PM)'}
                      </span>
                      <span className="text-[10px] text-astro-secondary-text font-mono">
                        07:15 PM, 06:42 AM
                      </span>
                    </div>
                    {timeFormatPref === '12h' && <Check className="w-4 h-4 text-astro-primary shrink-0" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSaveTimeFormatPref('24h')}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                      timeFormatPref === '24h'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-white hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div>
                      <span className="text-xs font-bold text-astro-primary block">
                        {isHi ? '24 घंटे (Railway / Military)' : '24-Hour (Military / Railway)'}
                      </span>
                      <span className="text-[10px] text-astro-secondary-text font-mono">
                        19:15, 06:42
                      </span>
                    </div>
                    {timeFormatPref === '24h' && <Check className="w-4 h-4 text-astro-primary shrink-0" />}
                  </button>
                </div>
              </div>

              {/* Custom Coordinates Collapsible */}
              <div className="pt-2 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setIsCustomCoordOpen(!isCustomCoordOpen)}
                  className="w-full flex items-center justify-between p-2.5 rounded-xl bg-astro-card hover:bg-[#FAF6ED] border border-astro-border text-xs font-bold text-astro-primary transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <Sliders className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'कस्टम अक्षांश एवं देशांतर सेट करें (Manual Coordinates)' : 'Manual High-Precision GPS Coordinates'}</span>
                  </span>
                  <span className="text-[10px] text-astro-secondary-text font-normal">
                    {isCustomCoordOpen ? (isHi ? 'छिपाएं' : 'Hide') : (isHi ? 'विस्तार करें' : 'Expand')}
                  </span>
                </button>

                {isCustomCoordOpen && (
                  <div className="p-3.5 mt-2 bg-white rounded-xl border border-astro-border space-y-3 animate-fade-in text-xs">
                    <div>
                      <label className="text-[11px] font-bold text-astro-primary block mb-1">
                        {isHi ? 'स्थान का नाम / लेबल' : 'Location Label / City Name'}
                      </label>
                      <input
                        type="text"
                        value={customPlaceNameStr}
                        onChange={(e) => setCustomPlaceNameStr(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border border-astro-border bg-astro-bg text-xs"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] font-bold text-astro-primary block mb-1">
                          {isHi ? 'अक्षांश (Latitude: -90° to +90°)' : 'Latitude (-90° to +90°)'}
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          value={customLatStr}
                          onChange={(e) => setCustomLatStr(e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border border-astro-border bg-astro-bg text-xs font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-astro-primary block mb-1">
                          {isHi ? 'देशांतर (Longitude: -180° to +180°)' : 'Longitude (-180° to +180°)'}
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          value={customLngStr}
                          onChange={(e) => setCustomLngStr(e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border border-astro-border bg-astro-bg text-xs font-mono"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleSaveCustomCoords}
                        className="px-4 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
                      >
                        {isHi ? 'सहेजें व लागू करें' : 'Save & Apply Coordinates'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Vedic Astrological Guidance Note */}
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl text-xs space-y-1 text-blue-950">
                <div className="flex items-center gap-1.5 font-bold text-[11px] text-blue-900">
                  <Info className="w-3.5 h-3.5 text-blue-700 shrink-0" />
                  <span>{isHi ? 'स्थान व मुहूर्त का शास्त्रीय नियम' : 'Vedic Principle: Geocentric vs. Topocentric Timings'}</span>
                </div>
                <p className="text-[11px] text-blue-900/90 leading-relaxed">
                  {isHi
                    ? 'तिथि, नक्षत्र और नवतारा वैश्विक खगोलीय घटनाएँ हैं जो पूरे विश्व में एक साथ सक्रिय रहती हैं। जबकि सूर्योदय, राहुकाल, अभिजीत मुहूर्त, चौघड़िया और लग्न आपके द्वारा चुने गए इस स्थान के अक्षांश व देशांतर के अनुसार सटीक रूप से बदलते हैं।'
                    : 'Tithi, Nakshatra, and Navtara are geocentric celestial alignments active universally at the exact same physical moment. Local events like Sunrise, Rahu Kaal, Abhijit Muhurta, Choghadiya, and Muhurta Ascendant recalculate to your chosen coordinates.'}
                </p>
              </div>
            </div>
          )}

          {/* SECTION 1: CHART STYLE (North, South, East) & WORKSTATION LAYOUT */}
          {activeSection === 'chartStyle' && (
            <div className="space-y-5">
              {/* Menu & Navigation Layout Selection (Horizontal vs Vertical) */}
              <div className="pb-5 border-b border-astro-border space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <div>
                    <h3 className="text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                      <LayoutGrid className="w-4 h-4 text-astro-accent" />
                      <span>{isHi ? 'मेनू एवं नेविगेशन लेआउट शैली' : 'Menu & Navigation Layout Style'}</span>
                    </h3>
                    <p className="text-xs text-astro-secondary-text">
                      {isHi
                        ? 'पारंपरिक क्षैतिज मेनू (डिफ़ॉल्ट) अथवा आधुनिक कोलेप्सिबल वर्टिकल साइडबार चुनें'
                        : 'Choose between the classic horizontal ribbon layout (default) or modern collapsible vertical sidebar'}
                    </p>
                  </div>
                  <span className="text-[10px] font-mono bg-astro-card px-2 py-0.5 rounded border border-astro-border text-astro-primary font-bold self-start sm:self-auto">
                    {menuLayout === 'horizontal' ? (isHi ? 'सक्रिय: क्षैतिज मेनू' : 'Active: Horizontal') : (isHi ? 'सक्रिय: वर्टिकल साइडबार' : 'Active: Vertical')}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Option 1: Horizontal Menu (Classic / Original - Default) */}
                  <button
                    type="button"
                    id="settings-layout-horizontal"
                    onClick={() => handleSelectMenuLayout('horizontal')}
                    aria-pressed={menuLayout === 'horizontal'}
                    className={`text-left p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-2.5 ${
                      menuLayout === 'horizontal'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-astro-primary/10 text-astro-primary">
                          <Sliders className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-xs sm:text-sm font-bold text-astro-primary">
                              {isHi ? 'क्षैतिज मेनू (Horizontal Menu)' : 'Horizontal Menu (Classic)'}
                            </h4>
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 border border-amber-300">
                              {isHi ? 'डिफ़ॉल्ट' : 'Default'}
                            </span>
                          </div>
                          <span className="text-[10px] text-astro-muted block">
                            {isHi ? 'पारंपरिक टॉप रिबन व डॉक' : 'Top ribbons & action dock'}
                          </span>
                        </div>
                      </div>
                      {menuLayout === 'horizontal' && (
                        <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    {/* Visual miniature representation of Horizontal Layout */}
                    <div className="p-2 rounded-lg bg-white/70 border border-astro-border/60 space-y-1">
                      <div className="h-2.5 w-full bg-[#0B3332] rounded flex items-center justify-between px-1">
                        <div className="h-1 w-6 bg-[#D4AF37] rounded-xs" />
                        <div className="flex gap-0.5">
                          <div className="h-1 w-1 bg-emerald-400 rounded-full" />
                          <div className="h-1 w-1 bg-amber-400 rounded-full" />
                          <div className="h-1 w-1 bg-indigo-400 rounded-full" />
                        </div>
                      </div>
                      <div className="h-2 w-full bg-[#E0EAE6] rounded flex items-center px-1 gap-1">
                        <div className="h-1 w-10 bg-[#082120] rounded-xs" />
                        <div className="h-1 w-8 bg-[#D4AF37] rounded-xs" />
                      </div>
                      <div className="h-5 w-full bg-[#FAF6ED] rounded border border-astro-border/40 flex items-center justify-center text-[9px] text-astro-secondary-text">
                        {isHi ? 'मुख्य कार्यक्षेत्र' : 'Main Workspace Canvas'}
                      </div>
                    </div>
                    <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                      {isHi
                        ? 'शीर्ष पर संपूर्ण 8-आइकन डॉक, जातक प्रोफाइल पट्टी एवं उपकरण/कार्यपीठ/वैदिक डेस्टिनी के क्षैतिज टैब।'
                        : 'Dedicated horizontal ribbons, workspace segmented control (Tools, Workbench, Vedic Destiny), and multi-chromatic header dock.'}
                    </p>
                  </button>

                  {/* Option 2: Vertical Sidebar (Modern Minimal) */}
                  <button
                    type="button"
                    id="settings-layout-vertical"
                    onClick={() => handleSelectMenuLayout('vertical')}
                    aria-pressed={menuLayout === 'vertical'}
                    className={`text-left p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-2.5 ${
                      menuLayout === 'vertical'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-astro-primary/10 text-astro-primary">
                          <LayoutGrid className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-xs sm:text-sm font-bold text-astro-primary">
                              {isHi ? 'वर्टिकल साइडबार (Vertical Sidebar)' : 'Vertical Sidebar (Minimal)'}
                            </h4>
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-teal-100 text-teal-800 border border-teal-300">
                              {isHi ? 'आधुनिक' : 'Modern'}
                            </span>
                          </div>
                          <span className="text-[10px] text-astro-muted block">
                            {isHi ? 'कोलेप्सिबल साइडबार व विस्तृत क्षितिज' : 'Collapsible menu & clean canvas'}
                          </span>
                        </div>
                      </div>
                      {menuLayout === 'vertical' && (
                        <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    {/* Visual miniature representation of Vertical Layout */}
                    <div className="p-2 rounded-lg bg-white/70 border border-astro-border/60 space-y-1">
                      <div className="h-2.5 w-full bg-[#0B3332] rounded flex items-center gap-1 px-1">
                        <div className="h-1.5 w-1.5 bg-[#D4AF37] rounded-full" />
                        <div className="h-1 w-full bg-[#174C4B] rounded-xs" />
                      </div>
                      <div className="flex gap-1 h-7">
                        <div className="w-6 h-full bg-[#071F1E] rounded flex flex-col items-center justify-between p-0.5">
                          <div className="w-3.5 h-1 bg-[#D4AF37] rounded-xs" />
                          <div className="w-3.5 h-1 bg-white/30 rounded-xs" />
                          <div className="w-3.5 h-1 bg-teal-400 rounded-xs" />
                        </div>
                        <div className="flex-1 h-full bg-[#FAF6ED] rounded border border-astro-border/40 flex items-center justify-center text-[9px] text-astro-secondary-text">
                          {isHi ? '100% कैनवास' : '100% Canvas'}
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                      {isHi
                        ? 'बाएं कोलेप्सिबल साइडबार (त्वरित क्रियाएं व मॉड्यूल), शीर्ष-बायां तीर टॉगल और विस्तृत जातक क्षितिज।'
                        : 'Collapsible left sidebar (w-0 to w-64), top-left arrow toggle, 100% canvas space, and redesigned minimal top horizon.'}
                    </p>
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-astro-primary font-serif">
                  {isHi ? 'कुंडली प्रदर्शन प्रारूप (Chart Rendering Style)' : 'Kundali Chart Rendering Style'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'यह सेटिंग संपूर्ण वर्कस्टेशन (बेसिक, प्रो, आचार्य) की सभी कुंडलियों पर लागू होगी।'
                    : 'This configuration applies universally across all single and multi-chart views in the workstation.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                {/* North Indian */}
                <button
                  type="button"
                  onClick={() => setChartStyle('north')}
                  aria-pressed={chartStyle === 'north'}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 ${
                    chartStyle === 'north'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-astro-accent block">
                        {isHi ? 'उत्तर भारतीय' : 'North Indian'}
                      </span>
                      <h4 className="text-sm font-bold text-astro-primary">
                        {isHi ? 'हीरा चक्र (Diamond)' : 'Diamond Chart'}
                      </h4>
                    </div>
                    {chartStyle === 'north' && (
                      <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <div className="w-16 h-16 mx-auto border border-astro-accent/50 bg-white rotate-45 flex items-center justify-center shadow-2xs">
                    <span className="text-[10px] font-bold text-astro-primary -rotate-45">1 Lagna</span>
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'भाव (Houses) स्थिर रहते हैं, राशियां (Signs) दक्षिणावर्त गति करती हैं।'
                      : 'Houses remain fixed at standard geometric positions; Zodiac signs rotate.'}
                  </p>
                </button>

                {/* South Indian */}
                <button
                  type="button"
                  onClick={() => setChartStyle('south')}
                  aria-pressed={chartStyle === 'south'}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 ${
                    chartStyle === 'south'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-astro-accent block">
                        {isHi ? 'दक्षिण भारतीय' : 'South Indian'}
                      </span>
                      <h4 className="text-sm font-bold text-astro-primary">
                        {isHi ? 'चौकोर चक्र (Box Grid)' : 'Box Grid Chart'}
                      </h4>
                    </div>
                    {chartStyle === 'south' && (
                      <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <div className="w-16 h-16 mx-auto border border-astro-accent/50 bg-white grid grid-cols-2 grid-rows-2 p-1 gap-1 shadow-2xs">
                    <div className="bg-[#FAF6ED] text-[9px] font-bold text-astro-primary flex items-center justify-center">Mesha</div>
                    <div className="bg-[#FAF6ED] text-[9px] font-bold text-astro-primary flex items-center justify-center">Vrish</div>
                    <div className="bg-[#FAF6ED] text-[9px] font-bold text-astro-primary flex items-center justify-center">Meena</div>
                    <div className="bg-[#FAF6ED] text-[9px] font-bold text-astro-primary flex items-center justify-center">Mithun</div>
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'राशियां (Signs) स्थिर रहती हैं; लग्न एवं भाव दक्षिणावर्त (Clockwise) बदलते हैं।'
                      : 'Zodiac signs remain fixed; Houses and Ascendant shift in clockwise order.'}
                  </p>
                </button>

                {/* East Indian */}
                <button
                  type="button"
                  onClick={() => setChartStyle('east')}
                  aria-pressed={chartStyle === 'east'}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 ${
                    chartStyle === 'east'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-astro-accent block">
                        {isHi ? 'पूर्वी भारतीय' : 'East Indian'}
                      </span>
                      <h4 className="text-sm font-bold text-astro-primary">
                        {isHi ? 'बंगाल / असम / ओडिशा चक्र' : 'Eastern Surya Chart'}
                      </h4>
                    </div>
                    {chartStyle === 'east' && (
                      <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <div className="w-16 h-16 mx-auto border border-astro-accent/50 bg-white p-1 flex items-center justify-center shadow-2xs">
                    <span className="text-[10px] font-bold text-astro-accent">सूर्य-चंद्र चक्र</span>
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'पारंपरिक पूर्वी भारतीय पद्धति, जिसमें राशियां वामावर्त (Anti-clockwise) चलती हैं।'
                      : 'Classical Eastern tradition where signs run in counter-clockwise order.'}
                  </p>
                </button>
              </div>

              {/* Degrees Toggle */}
              <div className="pt-3 border-t border-astro-border flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-astro-primary">
                    {isHi ? 'ग्रहों के अंश (Degrees) का प्रत्यक्ष प्रदर्शन' : 'Show Planetary Degrees on Charts'}
                  </h4>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'कुंडलियों में ग्रहों के साथ उनके सटीक अंश व कलाएं प्रदर्शित करें' : 'Display exact degrees and arcminutes beside planet symbols'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSaveShowDegrees(!showDegreesPref)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                    showDegreesPref
                      ? 'bg-astro-primary text-white border-astro-primary'
                      : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                  }`}
                >
                  {showDegreesPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                </button>
              </div>

              {/* Anti-Overlap Smart Layout & Density Mode Toggle */}
              <div className="pt-3 border-t border-astro-border flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-astro-primary">
                    {isHi ? 'स्मार्ट लेआउट व ओवरलैप रोकथाम (Anti-Overlap Smart Layout)' : 'Anti-Overlap Smart Chart Layout'}
                  </h4>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'सघन ग्रहों (Multi-planet conjunctions) के लिए स्वचालित फोंट व ग्रिड स्पेसिंग' : 'Automatically scale and space dense planetary conjunctions to prevent text overlapping'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSaveAntiOverlap(!antiOverlapPref)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                    antiOverlapPref
                      ? 'bg-astro-primary text-white border-astro-primary'
                      : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                  }`}
                >
                  {antiOverlapPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                </button>
              </div>

              {/* House Inspector Default View Style */}
              <div className="pt-3 border-t border-astro-border space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-astro-accent" />
                      {isHi ? 'भाव निरीक्षक दृश्य शैली (House Inspector View Style)' : 'House Inspector Default View Style'}
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text">
                      {isHi ? 'भाव विश्लेषण कार्डों का डिफ़ॉल्ट आरंभिक स्वरूप चुनें' : 'Choose default initial state for house inspection cards'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handleSaveInspectorViewPref('collapsed')}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                      inspectorViewPref === 'collapsed'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-astro-primary">
                        {isHi ? 'संक्षिप्त अवलोकन (Glance Mode)' : 'Glance / Executive Mode'}
                      </span>
                      {inspectorViewPref === 'collapsed' && <Check className="w-3.5 h-3.5 text-astro-primary" />}
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi ? 'कार्ड संक्षिप्त रूप में आरंभ होते हैं, मुख्य सारांश व बैज प्रदर्शित करते हैं' : 'Sections start collapsed showcasing key summary badges, ideal for quick consultations'}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveInspectorViewPref('expanded')}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                      inspectorViewPref === 'expanded'
                        ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                        : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-astro-primary">
                        {isHi ? 'विस्तृत शोध (Deep-Dive Mode)' : 'Deep-Dive Astrologer Mode'}
                      </span>
                      {inspectorViewPref === 'expanded' && <Check className="w-3.5 h-3.5 text-astro-primary" />}
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi ? 'सभी विस्तृत तालिकाएं, भिन्नाष्टकवर्ग एवं गोचर प्रत्यक्ष विस्तृत खुलते हैं' : 'All detailed tables, BAV grids, and live transits expand automatically'}
                    </p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SECTION: NAVTARA & NAKSHATRAS */}
          {activeSection === 'navtara' && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <h3 className="text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span>{isHi ? 'नवतारा चक्र एवं नक्षत्र सेटिंग्स' : 'Navtara Chakra & Nakshatra Settings'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text mt-0.5">
                  {isHi 
                    ? 'नक्षत्र गोचर, तारा बल, शुद्ध समय गणना (राहुकाल कटौती) एवं दृश्य प्रदर्शन को अनुकूलित करें।' 
                    : 'Customize Nakshatra transits, Tara Bala rules, net auspicious clean time deduction, and display preferences.'}
                </p>
              </div>

              <div className="bg-astro-card rounded-2xl border border-astro-border p-4 sm:p-5 space-y-4">
                {/* 1. Deduct Rahu Kalam */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      <span>{isHi ? 'राहुकाल कटौती (शुद्ध शुभ समय गणना)' : 'Deduct Rahu Kalam from Auspicious Window'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'सक्रिय शुभ तारा (सम्पत्, क्षेम, साधक, मित्र आदि) के समय में से आज के राहुकाल की अवधि स्वतः घटाएं ताकि केवल शुद्ध दोषरहित मुहूर्त शेष दिखे' 
                        : 'Automatically subtract Rahu Kalam interval from active benefic Tara window to display the net unblemished auspicious duration'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveDeductRahuKalam(!deductRahuKalamPref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      deductRahuKalamPref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {deductRahuKalamPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>

                {/* 2. Nakshatra Hover Card Toggle */}
                <div className="pt-4 border-t border-astro-border flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'नक्षत्र होवर अंतर्दृष्टि (Interactive Hover Insights)' : 'Nakshatra Interactive Hover Insights'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'नक्षत्रों पर माउस ले जाने पर चल/अचल प्रकृति, शास्त्रीय फल व श्रेष्ठ कार्यों का विवरण कार्ड दिखाएं' 
                        : 'Display rich popover cards on hover showing motion nature (Chara/Sthira), meaning & recommended activities'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveShowNavtaraHover(!showNavtaraHoverPref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      showNavtaraHoverPref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {showNavtaraHoverPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>

                {/* 3. Classical Motion Nature Badges */}
                <div className="pt-4 border-t border-astro-border flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Activity className="w-3.5 h-3.5 text-indigo-600" />
                      <span>{isHi ? 'शास्त्रीय गति बैज (चर/स्थिर/उग्र आदि)' : 'Nakshatra Motion Nature Badges'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'नक्षत्र तालिका में गति प्रकृति (चर, स्थिर, द्विस्वभाव, क्षिप्र, उग्र, तीक्ष्ण आदि) के सूक्ष्म बैज दिखाएं' 
                        : 'Show motion nature chips (Chara, Sthira, Dvisvabhava, Kshipra, Ugra, etc.) directly in the Navtara matrix'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveNavtaraMotionBadges(!navtaraMotionBadgesPref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      navtaraMotionBadgesPref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {navtaraMotionBadgesPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>

                {/* 4. Auto-collapse Reading Guide */}
                <div className="pt-4 border-t border-astro-border flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Timer className="w-3.5 h-3.5 text-amber-600" />
                      <span>{isHi ? 'शुभ-अशुभ मार्गदर्शिका स्वतः संक्षिप्त करें (25s टाइमर)' : 'Auto-collapse Benefic Guide Timer'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'पर्याप्त अध्ययन समय (25 सेकंड) के बाद 9-तारा मार्गदर्शिका स्वतः संक्षिप्त हो जाए (माउस रखने पर टाइमर रुकता है)' 
                        : 'Automatically collapse the 9-Tara reference guide after 25 seconds of reading time (pauses on hover)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveNavtaraAutoCollapseGuide(!navtaraAutoCollapseGuidePref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      navtaraAutoCollapseGuidePref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {navtaraAutoCollapseGuidePref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>

                {/* 5. Navtara Active Window Periodic Flash Toggle */}
                <div className="pt-4 border-t border-astro-border flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      <span>{isHi ? 'सक्रिय नक्षत्र आवधिक स्पंदन (Periodic Flash)' : 'Flash Active Transit Window'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'सक्रिय नक्षत्र तालिका सेल को प्रत्येक कुछ मिनटों में सौम्य रूप से चमकाएं (एक क्लिक से इस सत्र हेतु तुरंत बंद किया जा सकता है)' 
                        : 'Gently pulse and flash the active transit in the table every few minutes (single click dismisses for the session)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveNavtaraFlash(!navtaraFlashPref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      navtaraFlashPref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {navtaraFlashPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>

                {/* 6. Navtara Lord & Deity Details Toggle */}
                <div className="pt-4 border-t border-astro-border flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'स्वामी व देवता विवरण (Lord & Deity Info)' : 'Nakshatra Lord & Deity Details'}</span>
                    </h4>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">
                      {isHi 
                        ? 'नक्षत्र से संबंधित स्वामी व वैदिक देवता की पंक्ति प्रदर्शित करें (उदा. "Lord: Sun • Aryaman")' 
                        : 'Show detailed line for planetary lord and Vedic presiding deity (e.g. "Lord: Sun • Aryaman")'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveNavtaraLordDeity(!navtaraLordDeityPref)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      navtaraLordDeityPref
                        ? 'bg-astro-primary text-white border-astro-primary'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {navtaraLordDeityPref ? (isHi ? 'सक्रिय (ON)' : 'Enabled') : (isHi ? 'निष्क्रिय (OFF)' : 'Disabled')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {activeSection === 'language' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-astro-primary font-serif">
                  {isHi ? 'भाषा एवं शब्दावली चयन (Language & Terminology)' : 'Language & Terminology'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'अपनी पसंदीदा भाषा चुनें। शास्त्रीय संस्कृत पारिभाषिक शब्दों के साथ पूर्ण समन्वय।'
                    : 'Choose your preferred language. Complete bilingual support with classical Sanskrit terminology.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* English */}
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  aria-pressed={lang === 'en'}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                    lang === 'en'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🇬🇧</span>
                      <h4 className="text-sm font-bold text-astro-primary">English</h4>
                    </div>
                    <p className="text-xs text-astro-secondary-text">
                      International Vedic Jyotish terminology with Sanskrit diacritics.
                    </p>
                  </div>
                  {lang === 'en' && (
                    <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                  )}
                </button>

                {/* Hindi */}
                <button
                  type="button"
                  onClick={() => setLang('hi')}
                  aria-pressed={lang === 'hi'}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                    lang === 'hi'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🇮🇳</span>
                      <h4 className="text-sm font-bold text-astro-primary">हिन्दी (Hindi)</h4>
                    </div>
                    <p className="text-xs text-astro-secondary-text">
                      शुद्ध शास्त्रीय वैदिक फलित, पारम्परिक शब्दावली एवं व्याख्या।
                    </p>
                  </div>
                  {lang === 'hi' && (
                    <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* SECTION 3: AYANAMSHA & CALCULATION */}
          {activeSection === 'calculation' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-astro-primary font-serif">
                    {isHi ? 'अयनांश प्रणाली (Ayanamsha Calculation System)' : 'Ayanamsha Calculation System'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? 'ग्रह एवं भाव स्पष्ट हेतु प्रयुक्त खगोलीय अयनांश मान'
                      : 'Astronomical ayanamsha offset applied for Nirayana planetary longitudes'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleResetCalculationDefaults}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-astro-primary bg-astro-primary/5 hover:bg-astro-primary/10 border border-astro-primary/20 rounded-lg transition-colors cursor-pointer self-start sm:self-auto"
                  title={isHi ? 'मानक सेटिंग्स पुनर्स्थापित करें' : 'Restore Canonical Defaults'}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>{isHi ? 'मानक रीसेट' : 'Restore Canonical Defaults'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handleSaveAyanamsha('lahiri')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                    ayanamsha === 'lahiri'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-astro-primary">N.C. Lahiri</span>
                    {ayanamsha === 'lahiri' && <Check className="w-3.5 h-3.5 text-astro-primary" />}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'चित्रा पक्ष (भारत सरकार एवं प्रामाणिक मानक)' : 'Chitra Paksha (Government of India Official Standard)'}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleSaveAyanamsha('kp')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                    ayanamsha === 'kp'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-astro-primary">Krishnamurti (KP)</span>
                    {ayanamsha === 'kp' && <Check className="w-3.5 h-3.5 text-astro-primary" />}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'के.पी. पद्धति एवं उप-स्वामी विश्लेषण' : 'KP Stellar Astrology and Sub-Lord System'}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleSaveAyanamsha('raman')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                    ayanamsha === 'raman'
                      ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20'
                      : 'border-astro-border bg-astro-bg hover:bg-[#FAF6ED]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-astro-primary">B.V. Raman</span>
                    {ayanamsha === 'raman' && <Check className="w-3.5 h-3.5 text-astro-primary" />}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'डॉ. बी.वी. रमन शास्त्रीय मान' : 'Dr. B.V. Raman Classical Nirayana Values'}
                  </p>
                </button>
              </div>

              {/* Rahu-Ketu Node Configuration (ADR-4 Canonical Default: Mean Node) */}
              <div className="pt-3 border-t border-astro-border space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-astro-primary">
                    {isHi ? 'राहु / केतु पात गणना (Lunar Node Calculation)' : 'Rahu / Ketu Lunar Node Calculation'}
                  </h4>
                  <span className="text-[10px] font-semibold text-astro-secondary-text">
                    {nodeType === 'mean' 
                      ? (isHi ? 'मानक: मध्यम पात' : 'Standard: Mean Node') 
                      : (isHi ? 'वैकल्पिक: वास्तविक पात' : 'Optional: True Node')}
                  </span>
                </div>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi
                    ? 'पारंपरिक वैदिक ज्योतिष एवं मानक पंचांग मध्यम पात (Mean Node) का उपयोग करते हैं। खगोलीय दोलनों को देखने हेतु वास्तविक पात (True Node) का चयन किया जा सकता है।'
                    : 'Classical Jyotish and standard panchangas canonically use Mean Node. True Node incorporates instantaneous astronomical osculating perturbations.'}
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                  <label className="flex items-center gap-2 text-xs text-astro-text cursor-pointer">
                    <input
                      type="radio"
                      name="nodeType"
                      value="mean"
                      checked={nodeType === 'mean'}
                      onChange={() => handleSaveNodeType('mean')}
                      className="accent-astro-primary"
                    />
                    <span className="font-semibold">{isHi ? 'मध्यम पात (Mean Node - अनुशंसित / मानक)' : 'Mean Node (Recommended / Canonical Default)'}</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-astro-text cursor-pointer">
                    <input
                      type="radio"
                      name="nodeType"
                      value="true"
                      checked={nodeType === 'true'}
                      onChange={() => handleSaveNodeType('true')}
                      className="accent-astro-primary"
                    />
                    <span>{isHi ? 'वास्तविक पात (True / Osculating Node)' : 'True / Osculating Node (Instantaneous)'}</span>
                  </label>
                </div>
              </div>

              {/* Ask Acharya Floating Widget Setting */}
              <div className="pt-3 border-t border-astro-border space-y-2">
                <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-astro-accent" />
                  {isHi ? 'एआई आचार्य फ्लोटिंग विजेट (Ask Acharya Widget)' : 'Ask Acharya AI Floating Widget'}
                </h4>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi
                    ? 'प्रो, आचार्य और सलाहकार प्रोफाइल में फ्लोटिंग एआई आचार्य बटन प्रदर्शित करें (बेसिक और गेस्ट उपयोगकर्ताओं के लिए यह हमेशा नीचे दाएं कोने में सक्रिय रहता है)।'
                    : 'Display the floating Ask Acharya AI consultant button in Pro, Astrologer, and Consultant profiles. (Always active for Basic and Guest users at bottom right).'}
                </p>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-astro-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAskAcharyaPref}
                      onChange={(e) => handleSaveShowAskAcharya(e.target.checked)}
                      className="rounded border-astro-border text-astro-primary focus:ring-astro-primary"
                    />
                    <span className="font-medium">
                      {isHi ? 'फ्लोटिंग Ask Acharya विजेट दिखाएं' : 'Show Ask Acharya Floating Widget'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 4: LEGACY DASHBOARD */}
          {activeSection === 'legacy' && onOpenLegacyDashboard && isSuperAdmin && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-astro-primary font-serif">
                  {isHi ? 'लेगेसी वर्कस्टेशन डैशबोर्ड' : 'Legacy Workstation Dashboard'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'पारंपरिक लेगेसी डैशबोर्ड दृश्य पर स्विच करें।'
                    : 'Access the classical fallback tab-based dashboard layout.'}
                </p>
              </div>

              <div className="space-y-3">
                {/* Switch to Legacy Dashboard */}
                <div className="p-4 rounded-xl border border-astro-border bg-[#FAF6ED] flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-astro-primary flex items-center gap-1.5">
                      <RotateCcw className="w-4 h-4 text-astro-accent" />
                      <span>{isHi ? 'लेगेसी डैशबोर्ड व्यू (Legacy Fallback Dashboard)' : 'Legacy Fallback Dashboard'}</span>
                    </h4>
                    <p className="text-xs text-astro-secondary-text mt-0.5">
                      {isHi
                        ? 'यदि आप क्लासिक टैब-आधारित लेगेसी इंटरफेस देखना चाहते हैं तो यहां क्लिक करें।'
                        : 'Switch to the classical tab-based legacy dashboard interface.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenLegacyDashboard();
                    }}
                    className="px-3.5 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold transition-all shadow-2xs cursor-pointer whitespace-nowrap"
                  >
                    {isHi ? 'लेगेसी खोलें' : 'Open Legacy'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 border-t border-astro-border bg-[#FAF6ED]/70 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-astro-secondary-text">
              {isHi ? 'सेटिंग्स तुरंत लागू होती हैं एवं स्वतः सहेजी जाती हैं।' : 'Settings apply instantaneously and auto-save locally.'}
            </span>
            {onOpenVersionModal ? (
              <button
                type="button"
                id="settings-btn-version-history"
                onClick={() => {
                  onOpenVersionModal();
                }}
                title={isHi ? 'गिट वर्शन एवं 6-संस्करण इतिहास देखें' : 'View Git Commit SHA & 6-Version History'}
                className="font-mono text-[10.5px] text-astro-primary bg-white/80 hover:bg-white dark:bg-astro-card hover:border-astro-accent/60 px-2 py-0.5 rounded border border-astro-border transition cursor-pointer flex items-center gap-1 shadow-2xs font-semibold"
              >
                <span>v{getAppVersionInfo().version}</span>
                {getAppVersionInfo().gitCommit && getAppVersionInfo().gitCommit !== 'dev' && (
                  <span className="text-[9px] opacity-75 font-mono">({getAppVersionInfo().gitCommit})</span>
                )}
                <span className="text-[10px] text-astro-accent">↗</span>
              </button>
            ) : (
              <span className="font-mono text-[10px] text-astro-secondary-text bg-white/60 dark:bg-astro-card px-1.5 py-0.5 rounded border border-astro-border">
                {getAppVersionInfo().displayVersion}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold transition-colors cursor-pointer shadow-xs"
          >
            {isHi ? 'पूर्ण (Done)' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
};
